import { FC, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import Button from '@components/common/button';
import { useForm } from '@hooks/helpers/useForm';
import { FormProvider } from 'react-hook-form';
import FormField from '@components/form_field';
import { apiContactService } from '@api';
import { EContactType, TContact, TCreateContact } from '@xeppt/xeppt-sdk/types';
import { useLocales } from '@hooks/helpers/useLocales';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useDebounceValue } from 'usehooks-ts';
import { getFirstLetter, highlightSearchText } from '@utils/index';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import emptySearchIllustration from '@svg/illustrations/empty_serch.svg';
import Skeleton from '@components/common/skeleton';
import { Icon } from '@components/icons';

interface IProps {
    handleBack: () => void;
    isEdit: boolean;
    editableContact?: TContact;
}

const Contact: FC<IProps> = ({ handleBack, editableContact, isEdit }) => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'send_and_request.manage_contacts'
    });
    const [tabType] = useQueryParams(['type']);
    const form = useForm({});
    const {
        requestSuccessLocale,
        requestErrorLocale,
        labelLocale,
        validationLocale,
        placeholderLocale,
        submitLocale
    } = useLocales();
    const [requestSearch, setRequestSearch] = useDebounceValue('', 500);
    const [search, setSearch] = useState('');
    const [type, setType] = useState<EContactType>(EContactType.INTERNAL);

    useEffect(() => {
        setRequestSearch(search);
    }, [search]);

    useEffect(() => {
        if (tabType) {
            //@ts-ignore
            setType(Number(tabType) as EContactType);
            setSearch('');
        }
    }, []);

    const { data: searchContacts, isLoading: isSearchLoading } = useApiQuery({
        method: () => apiContactService.searchContact(requestSearch),
        deps: [requestSearch],
        condition: requestSearch.length >= 3
    });

    const { handleRequest: handleSaveContact } = useApiMutation({
        method: (val: string) => {
            return apiContactService.addContact(val);
        },
        onSuccess: () => {
            handleBack();
            requestSuccessLocale('add_contact');
        },
        onError: requestErrorLocale
    });

    useEffect(() => {
        if (!!editableContact) {
            form.reset(editableContact);
            setType(EContactType.EXTERNAL);
        }
    }, []);

    const { handleRequest: handleCreateContact, isLoading } = useApiMutation({
        method: () => {
            const values = form.getValues();
            const data: TCreateContact = {
                first_name: values?.first_name,
                last_name: values?.last_name,
                phone: values?.phone || undefined,
                email: values?.email
            };
            return apiContactService.createContact(data);
        },
        onSuccess: () => {
            requestSuccessLocale('create_contact');
            handleBack();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleUpdateContact, isLoading: isUpdateContactLoading } =
        useApiMutation({
            method: () => {
                const values = form.getValues();
                const data: TCreateContact = {
                    first_name: values?.first_name,
                    last_name: values?.last_name,
                    email: values?.email || undefined,
                    phone: values?.phone || undefined
                };
                return apiContactService.updateContact(editableContact?.id || '', data);
            },
            onSuccess: () => {
                if (!!editableContact) {
                    requestSuccessLocale('update_contact');
                } else {
                    requestSuccessLocale('create_contact');
                }
                handleBack();
            },
            onError: requestErrorLocale
        });

    const onSubmit = () => {
        form.trigger().then((isValid) => {
            if (isValid) {
                if (editableContact) {
                    handleUpdateContact(undefined);
                } else {
                    handleCreateContact(undefined);
                }
            }
        });
    };

    return (
        <FormProvider {...form}>
            <motion.div {...pageAnimation} className={styles.add_contact_wrapper}>
                <Typography variant="body3" className={styles.description}>
                    {!!editableContact
                        ? t('update_contact_description')
                        : type === EContactType.INTERNAL
                          ? t('add_contact_description')
                          : t('add_contact_description_external')}
                </Typography>
                {type === EContactType.INTERNAL && !editableContact && (
                    <div className={styles.content}>
                        <Input
                            value={search}
                            leftIcon="search"
                            rightIcon={search.length > 0 ? 'filed_close' : undefined}
                            onClickRightIcon={() => setSearch('')}
                            placeholder={placeholderLocale('search_contact_info')}
                            onChange={(val) => setSearch(val)}
                            full
                        />
                        {((search &&
                            (searchContacts?.suggestions?.length === 0 ||
                                !searchContacts?.suggestions) &&
                            searchContacts?.contacts?.length === 0) ||
                            !search) && (
                            <div
                                className={styles.add_new}
                                onClick={() => setType(EContactType.EXTERNAL)}>
                                <div className={styles.left}>
                                    <div className={styles.avatar}>
                                        <Icon name="external_person" />
                                    </div>{' '}
                                    Add external contact
                                </div>
                                <Icon className={styles.icon} name="nav_right" />
                            </div>
                        )}

                        {!isSearchLoading &&
                        search &&
                        searchContacts?.suggestions?.length === 0 &&
                        searchContacts?.contacts?.length === 0 ? (
                            <div className={styles.empty_search}>
                                <img src={emptySearchIllustration} alt="empty search" />
                                <div className={styles.title}>No results found</div>
                                <div className={styles.description}>
                                    Please check if there are any spelling mistakes
                                </div>
                            </div>
                        ) : (
                            <div className={styles.result_wrapper}>
                                {!!search && (searchContacts?.suggestions?.length || 0) > 0 && (
                                    <div className={styles.result}>
                                        {isSearchLoading ? (
                                            <Skeleton className={styles.skeleton} />
                                        ) : (
                                            searchContacts?.suggestions?.map((contact) => {
                                                return (
                                                    <div
                                                        className={styles.contact}
                                                        key={contact.user_id}>
                                                        <div className={styles.left}>
                                                            <div className={styles.avatar}>
                                                                {getFirstLetter(
                                                                    contact?.first_name
                                                                )}
                                                                {getFirstLetter(contact?.last_name)}
                                                            </div>
                                                            <div className={styles.text_wrapper}>
                                                                <div
                                                                    className={styles.title}
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: highlightSearchText(
                                                                            `${contact.first_name} ${contact.last_name}`,
                                                                            search
                                                                        )
                                                                    }}
                                                                />
                                                                <div
                                                                    className={styles.description}
                                                                    dangerouslySetInnerHTML={{
                                                                        __html: highlightSearchText(
                                                                            `@${contact.username}`,
                                                                            search
                                                                        )
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <Button
                                                            className={styles.add_button}
                                                            variant="outlined-gold"
                                                            size="small"
                                                            onClick={() =>
                                                                handleSaveContact(contact.user_id)
                                                            }>
                                                            {submitLocale('add_contact')}
                                                        </Button>
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                                {(searchContacts?.contacts?.length || 0) > 0 && (
                                    <div className={styles.result}>
                                        <div className={styles.result_length}>
                                            Existing contacts
                                        </div>
                                        {searchContacts?.contacts.map((contact) => {
                                            return (
                                                <div className={styles.contact} key={contact.id}>
                                                    <div className={styles.left}>
                                                        <div className={styles.avatar}>
                                                            {getFirstLetter(contact?.first_name)}
                                                            {getFirstLetter(contact?.last_name)}
                                                        </div>
                                                        <div className={styles.text_wrapper}>
                                                            <div
                                                                className={styles.title}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: highlightSearchText(
                                                                        `${contact.first_name} ${contact.last_name}`,
                                                                        search
                                                                    )
                                                                }}
                                                            />
                                                            <div
                                                                className={styles.description}
                                                                dangerouslySetInnerHTML={{
                                                                    __html: highlightSearchText(
                                                                        contact.email
                                                                            ? contact.email
                                                                            : //@ts-ignore
                                                                              contact?.username
                                                                              ? `@${contact?.username}`
                                                                              : //@ts-ignore
                                                                                `+${contact?.phone}`,
                                                                        search
                                                                    )
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
                {type === EContactType.EXTERNAL && (
                    <div className={styles.add_interac}>
                        <FormField
                            name="first_name"
                            renderComponent={(props) => (
                                <Input
                                    label={labelLocale('first_name')}
                                    full
                                    {...props}
                                    placeholder="Enter first name"
                                />
                            )}
                            rules={{
                                required: {
                                    value: true,
                                    message: validationLocale('first_name')
                                }
                            }}
                        />
                        <FormField
                            name="last_name"
                            renderComponent={(props) => (
                                <Input
                                    label={labelLocale('last_name')}
                                    full
                                    {...props}
                                    placeholder="Enter last name"
                                />
                            )}
                            rules={{
                                required: {
                                    value: true,
                                    message: validationLocale('last_name')
                                }
                            }}
                        />
                        <FormField
                            name="email"
                            renderComponent={(props) => (
                                <Input
                                    label={labelLocale('email')}
                                    full
                                    {...props}
                                    placeholder="Enter email"
                                />
                            )}
                            rules={{
                                required: {
                                    value:
                                        (editableContact &&
                                            editableContact.type === EContactType.EXTERNAL) ||
                                        !editableContact,
                                    message: validationLocale('email')
                                }
                            }}
                        />
                        <FormField
                            name="phone"
                            renderComponent={(props) => (
                                <Input
                                    label={labelLocale('enter_contact_phone') + ' (optional)'}
                                    full
                                    {...props}
                                    mask="1 (999) 999-9999"
                                    placeholder="Enter phone number"
                                />
                            )}
                            rules={{
                                validate: (value: string) => {
                                    if (value && value?.length !== 10) {
                                        return validationLocale('phone');
                                    }
                                }
                            }}
                        />
                    </div>
                )}
                <div className={styles.actions}>
                    {type === EContactType.EXTERNAL && !isEdit && (
                        <Button
                            variant="outlined-primary"
                            onClick={() => setType(EContactType.INTERNAL)}
                            leftIcon="arrow_left">
                            Back to existing users
                        </Button>
                    )}
                    {(type === EContactType.EXTERNAL || !!editableContact) && (
                        <Button
                            variant="primary"
                            onClick={onSubmit}
                            isLoading={isLoading || isUpdateContactLoading}>
                            {t(!!editableContact ? 'update_contact_submit' : 'add_contact_submit')}
                        </Button>
                    )}
                </div>
            </motion.div>
        </FormProvider>
    );
};

export default Contact;
